import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Releases the cart drawer focus trap when the COD app opens its form.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController} */
  #codBridgeAbortController = new AbortController();

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.#codBridgeAbortController.signal.aborted) this.#codBridgeAbortController = new AbortController();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    this.#dialog?.addEventListener('click', this.#handleCodTriggerClick, {
      capture: true,
      signal: this.#codBridgeAbortController.signal,
    });

    this.#prepareCodDrawer();

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#codBridgeAbortController.abort();
  }

  #prepareCodDrawer = () => {
    const nativeCheckout = this.querySelector('#checkout[name="checkout"], .cart__checkout-button[name="checkout"]');
    if (nativeCheckout instanceof HTMLElement) {
      nativeCheckout.hidden = true;
      nativeCheckout.setAttribute('aria-hidden', 'true');
      nativeCheckout.setAttribute('tabindex', '-1');
    }

    for (const element of this.querySelectorAll(
      '.additional-checkout-buttons, .cart__additional-checkout-buttons, shopify-accelerated-checkout-cart'
    )) {
      if (element instanceof HTMLElement) {
        element.hidden = true;
        element.setAttribute('aria-hidden', 'true');
      }
    }
  };

  /** @param {Element} element */
  #isCodTrigger(element) {
    if (element.matches('.orichi-cod-button, [id="orichi-cod-button"]')) return true;
    if (!element.matches('button, a, [role="button"], input[type="button"], input[type="submit"]')) return false;
    if (element.matches('#checkout, [name="checkout"], .cart__checkout-button')) return false;

    const htmlElement = /** @type {HTMLElement} */ (element);
    const label = [
      htmlElement.id,
      htmlElement.className,
      htmlElement.getAttribute('aria-label'),
      htmlElement.getAttribute('title'),
      htmlElement.getAttribute('data-testid'),
      htmlElement.getAttribute('data-action'),
      htmlElement.textContent,
      element instanceof HTMLInputElement ? element.value : '',
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return /madgic|orichi|cash\s*on\s*delivery|\bcod\b|الدفع\s*عند\s*الاستلام|اطلب\s*الآن/.test(label);
  }

  /** @param {MouseEvent} event */
  #handleCodTriggerClick = async (event) => {
    const path = event.composedPath();
    const trigger = path.find((node) => node instanceof Element && this.#isCodTrigger(node));
    if (!(trigger instanceof HTMLElement)) return;

    if (!this.#themeDrawer?.isOpen) return;
    // Let the app receive the real click while releasing the cart focus trap.
    // Replaying a click on a now-hidden trigger is ignored by the COD app.
    await this.#themeDrawer.close();
    const modal = document.querySelector('#orichi-cod-order-modal');
    if (modal instanceof HTMLElement) {
      modal.querySelector('input:not([type="hidden"]), select, textarea')?.focus();
    }
  };

  #handleDrawerOpen = () => {
    this.#prepareCodDrawer();
    this.#updateStickyState();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => {
            this.#prepareCodDrawer();
            this.#updateStickyState();
          });

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
